import type { CSSProperties, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import { MChevronLeft } from './icons'
import { BACK_STATE, backTarget } from './nav'

// ============================================================================
// THE SHELL PRIMITIVES — docs/design/handoff-mobile/README.md §Shell.
//
// Header band, filter chip row, scroll container, card. Four things, because
// every screen in the bundle is those four things stacked, and writing them once
// is what keeps the 14px page gutter and the scroll-padding rule from being
// re-typed (and mistyped) on five screens.
//
// THE SAFE AREAS ARE REAL env() INSETS, NOT THE MOCKUP'S DRAWN CHROME. The
// mockup paints a 30px status bar and a 24px gesture pill "for realism"; the
// README says both belong to the OS in the installed app ("use viewport-fit=cover
// and env(safe-area-inset-top)"). So the band reserves the top inset and the tab
// bar reserves the bottom one, and neither draws a fake clock or a fake pill —
// an app that paints its own status bar is an app with two of them.
// ============================================================================

/** The four bands the phone has. Each resolves to a `--band-mobile-*` custom property. */
export type MobileBand = 'today' | 'school' | 'tasks' | 'course'

/**
 * THE SCROLL CONTAINER'S BOTTOM PADDING.
 *
 * README §Shell says "pad the bottom by `64 + 24 + 12` px so the last row clears the tab bar and
 * the capture button" — a rule written for a tab bar that OVERLAYS the content. Ours does not:
 * the V10 nav layer (mobile/shell/NavStage.tsx) gives every tab page a bottom padding of the
 * floating bar's own height plus its gap above the safe area (`--m-bar-space` in index.css), so
 * content already ends above it. Padding for the bar again produced ~100px of nothing at the
 * bottom of every screen — invisible on a long list, and a plain hole under the chat composer,
 * which is fixed-height (owner's screenshot, 2026-09-12). The 32px that remains is the breathing
 * room the screens were laid out with (it once cleared a capture button riding 20px above the
 * old bar), and it stays until each screen's own V10 phase redraws it.
 */
export const SCROLL_BOTTOM = '32px'

/**
 * The distance from the PAGE's bottom edge to just above the tab bar — for things positioned
 * `absolute` against the page (the Reminders undo toast), which do sit over the bar's space and
 * therefore do need its height: the bar (`--m-bar-h`), its gap above the safe area
 * (`--m-bar-gap`), the inset, plus a 12px gap — all in the bar's own unzoomed pixels, so divided
 * by the app's zoom to land in the page's (see `--m-bar-space` in index.css).
 */
export const ABOVE_TAB_BAR =
  'calc((var(--m-bar-h) + var(--m-bar-gap) + 12px + env(safe-area-inset-bottom, 0px)) / var(--app-zoom, 1))'

/**
 * The tinted strip every screen but Chat opens with. `padding:10px 18px 12–14px`
 * plus whatever the OS is keeping at the top of the display.
 *
 * Chat has NO band (README §Header band: "it uses a solid white header with a
 * bottom border, because its scope row needs a stable ground"), which is why that
 * screen builds its own header instead of passing a hue here.
 */
export function Band({
  band,
  className,
  children,
}: {
  band: MobileBand
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={clsx('flex-none px-[18px] pb-[14px] pt-[10px]', className)}
      style={{
        background: `var(--band-mobile-${band})`,
        paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Screen title `600 19px/1.15 −0.02em`, then the quiet `·`-separated subline.
 *
 * `action` IS ON THE SUBLINE ROW, NOT THE TITLE ROW, and that is a measurement rather than a
 * preference. The title truncates by design, and the owner runs the app at 130% (Appearance →
 * Size, his default), which makes a 390px phone a 300px CSS viewport: measured on Today at that
 * scale, "Wednesday, September 16" needs 236px and gets 264 — it fits with 28px to spare, and a
 * 40px tile plus its 12px gap beside it costs 52. Putting the tile on the title's row therefore
 * eats the date's day number on the home screen, which is one of the few facts that screen exists
 * to state. On the subline's row it costs nothing: that row is small type that already wraps.
 *
 * Bands with no action render exactly what they rendered before — the row is the same row.
 */
export function BandHeading({
  title,
  subline,
  action,
}: {
  title: string
  subline?: ReactNode
  action?: ReactNode
}) {
  const hasSubline = subline !== undefined && subline !== null
  return (
    <div className="min-w-0 flex-1">
      <h1 className="truncate text-[19px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink">
        {title}
      </h1>
      {(hasSubline || action) && (
        <div className="mt-[5px] flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[7px] text-[11.5px] leading-none text-muted">
            {subline}
          </div>
          {action}
        </div>
      )}
    </div>
  )
}

/** The 3px dot the sublines and metadata lines use as a separator. */
export function Dot() {
  return <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
}

/**
 * The optional 40px action tile at the right of a band: radius 11, a white wash,
 * a 1px line. README §Header band names it, and it is only ever drawn when it has
 * something real to do.
 */
export function BandTile({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-10 flex-none items-center justify-center rounded-row border border-line-2 bg-surface/80 text-ink-2 active:bg-fill"
    >
      {children}
    </button>
  )
}

/**
 * The filter chip row. `padding:0 14px 10px`, horizontal scroll, 7px gap; chips
 * 36px tall with `padding:0 14px`, radius 20.
 *
 * `overflow-x:auto` with no visible scrollbar, because a phone has no pointer to
 * grab one with — the partly-visible last chip is the affordance, exactly as the
 * mockup draws it.
 */
export function ChipRow({ children }: { children: ReactNode }) {
  return (
    <div
      role="tablist"
      className="flex flex-none gap-[7px] overflow-x-auto px-[14px] pb-[10px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {children}
    </div>
  )
}

/** One chip. Active is an `ink` fill; a count inside is a 20-radius danger pill. */
export function Chip({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean
  count?: number | null
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={clsx(
        'flex h-9 flex-none items-center gap-[7px] rounded-pill px-[14px] text-[12.5px] leading-none',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        // `text-surface`, NOT `text-on-dark`. The emphatic tile INVERTS with the
        // theme — `bg-ink` is a near-black chip in light mode and a near-white
        // one in dark — so the label on it has to invert too. `on-dark` is
        // invariant white and belongs to surfaces that are dark in both themes
        // (the recording screen, a solid status button); on this chip it would
        // be white type on a white chip at night. The desktop rail's logo makes
        // exactly this pairing.
        active
          ? 'bg-ink font-semibold text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]'
          : 'border border-line bg-surface font-medium text-ink-2 active:bg-fill',
      )}
    >
      {children}
      {typeof count === 'number' && count > 0 && (
        <span className="rounded-pill bg-danger-line-2 px-[6px] py-px text-[9.5px] font-semibold leading-[1.5] text-danger-dot">
          {count}
        </span>
      )}
    </button>
  )
}

/**
 * The one scroll region per screen, between the chip row and the tab bar.
 * `padding:0 14px 14px`, column, 12px gap — plus the bottom clearance above.
 */
export function ScrollColumn({
  className,
  style,
  children,
}: {
  className?: string
  style?: CSSProperties
  children: ReactNode
}) {
  return (
    <div
      className={clsx(
        'flex min-h-0 flex-1 flex-col gap-[12px] overflow-y-auto overflow-x-hidden px-[14px]',
        className,
      )}
      style={{ paddingBottom: SCROLL_BOTTOM, ...style }}
    >
      {children}
    </div>
  )
}

/** A context card: white, radius 16, 1px line, `padding:14px 16px`. */
export function Card({
  className,
  style,
  children,
}: {
  className?: string
  style?: CSSProperties
  children: ReactNode
}) {
  return (
    <section
      className={clsx(
        'flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-4 py-[14px]',
        className,
      )}
      style={style}
    >
      {children}
    </section>
  )
}

/**
 * A card's header row: a 16px domain glyph, a `600 12.5px` title, and an
 * optional right-aligned qualifier or pill.
 */
export function CardHeader({
  icon,
  title,
  right,
}: {
  icon: ReactNode
  title: string
  right?: ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-[12.5px] font-semibold leading-none text-ink-2">{title}</span>
      {right && <span className="ml-auto flex-none">{right}</span>}
    </div>
  )
}

/** `600 10px/.08em` uppercase — the eyebrow, at the mobile scale. */
export function Eyebrow({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-semibold uppercase leading-none tracking-[0.08em]',
        className ?? 'text-microcopy',
      )}
    >
      {children}
    </span>
  )
}

/**
 * A skeleton block matching a row's geometry. README §Loading: "Skeletons
 * matching final row geometry. Never a spinner over readable content."
 */
export function Skeleton({ height, className }: { height: number; className?: string }) {
  return (
    <div
      className={clsx('animate-pulse rounded-row bg-fill', className)}
      style={{ height }}
      aria-hidden
    />
  )
}

/**
 * A read that failed, said in the reason's own words.
 *
 * Never an empty list in its place: an empty list is a claim ("you have nothing
 * here") and a failed read is not entitled to make it.
 */
export function ReadFailure({ error, what }: { error: unknown; what: string }) {
  return (
    <p className="text-[11.5px] leading-[1.5] text-microcopy text-pretty">
      {what} could not be loaded, so nothing is shown in its place — an empty list here would
      claim there is nothing.{' '}
      {error instanceof Error ? error.message : ''}
    </p>
  )
}

/**
 * BACK FROM A DETAIL SCREEN. With the V10 bar hidden on every pushed screen, this is the way out,
 * so it has to work on a cold open as well as after an in-app push: one step back when the router
 * put an entry behind this page, otherwise the tab's own screen, replacing this one (the rule is
 * `backTarget` in mobile/shell/nav.ts). Both paths animate as a back (board 25).
 */
export function useBack(): () => void {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  return () => {
    const state = window.history.state as { idx?: unknown } | null
    const target = backTarget(pathname, {
      length: window.history.length,
      idx: typeof state?.idx === 'number' ? state.idx : null,
    })
    if (target.kind === 'history') navigate(-1)
    else navigate(target.to, { replace: true, state: BACK_STATE })
  }
}

/** The chevron-and-word back control the course page has always had, now shared by every detail. */
export function BackRow({ label }: { label: string }) {
  const back = useBack()
  return (
    <button
      type="button"
      onClick={back}
      className="flex h-11 items-center gap-[6px] text-ink-2 active:bg-fill"
    >
      <MChevronLeft />
      <span className="text-[12.5px] font-medium leading-none text-ink-3">{label}</span>
    </button>
  )
}
